import { LocationOn as LocationIcon } from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	CircularProgress,
	TextField,
	TextFieldProps,
	Typography
} from "@mui/material";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface PlacesAutocompleteProps extends Omit<TextFieldProps, "onChange" | "value"> {
	onChange: (inputValue: google.maps.places.PlaceResult | null) => void;
	converter?: (
		place: google.maps.places.PlaceResult
	) => { id: string; label: string } | null;
	value: google.maps.places.PlaceResult | null;
	types?: string[];
	componentRestrictions?: google.maps.places.ComponentRestrictions;
	placeholder?: string;
	disabled?: boolean;
	showFormatHint?: boolean;
	loading?: boolean;
	fields?: string[];
	minInputLength?: number;
	startIcon?: React.ReactNode;
}

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
	onChange,
	converter,
	value,
	types = ["(cities)"],
	componentRestrictions = { country: [] },
	placeholder,
	disabled = false,
	showFormatHint = false,
	loading = false,
	fields = ["address_components", "formatted_address", "name", "place_id"],
	minInputLength = 3,
	startIcon,
	...textFieldProps
}) => {
	const { t, i18n } = useTranslation();

	// Memoize fields to prevent useEffect dependency changes
	const memoizedFields = useMemo(() => fields, [fields]);

	// State management
	const [inputValue, setInputValue] = useState("");
	const [options, setOptions] = useState<google.maps.places.PlaceResult[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	// Refs for Google Maps objects
	const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
	const placesService = useRef<google.maps.places.PlacesService | null>(null);
	const onChangeRef = useRef(onChange);
	const currentPlaceIdRef = useRef<string | null>(null);
	const previousLanguageRef = useRef<string>(i18n.language);

	// Keep the onChange ref up to date
	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	// Get the Places library
	const placesLib = useMapsLibrary("places");

	// Sync input value with selected value
	useEffect(() => {
		if (value?.formatted_address) {
			setInputValue(value.formatted_address);
		} else if (value?.name) {
			setInputValue(value.name);
		} else if (!value) {
			setInputValue("");
		}
		// Update the ref to track current place_id
		currentPlaceIdRef.current = value?.place_id || null;
	}, [value]);

	// Refresh place details when language changes (using ref to avoid infinite loop)
	useEffect(() => {
		// Only run if language actually changed
		if (previousLanguageRef.current !== i18n.language) {
			const placeId = currentPlaceIdRef.current;
			if (placeId && placesService.current) {
				placesService.current.getDetails(
					{
						placeId: placeId,
						fields: memoizedFields,
						language: i18n.language
					},
					(place, status) => {
						if (status === google.maps.places.PlacesServiceStatus.OK && place) {
							// Use the ref to avoid infinite loop
							onChangeRef.current(place);
						}
					}
				);
			}
			// Update the previous language ref
			previousLanguageRef.current = i18n.language;
		}
	}, [i18n.language, memoizedFields]);

	// Refresh place details when a new value is set and services are ready
	useEffect(() => {
		const placeId = value?.place_id;
		const currentFormattedAddress = value?.formatted_address;
		if (placeId && placesService.current && placesLib) {
			placesService.current.getDetails(
				{
					placeId: placeId,
					fields: memoizedFields,
					language: i18n.language
				},
				(place, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK && place) {
						// Only update if the place details are different (to avoid infinite loop)
						if (
							converter?.(place)?.label === currentFormattedAddress ||
							place.formatted_address === currentFormattedAddress
						) {
							return;
						}

						onChangeRef.current(place);
					}
				}
			);
		}
	}, [value, converter, placesLib, memoizedFields, i18n.language]);

	// Clear options when language changes to force fresh search
	useEffect(() => {
		setOptions([]);
	}, [i18n.language]);

	// Initialize Google Places services
	useEffect(() => {
		if (!placesLib || disabled) {
			return;
		}

		try {
			// Initialize AutocompleteService for predictions
			autocompleteService.current = new placesLib.AutocompleteService();

			// Initialize PlacesService for getting place details
			// We need a dummy div for PlacesService
			const dummyDiv = document.createElement("div");
			placesService.current = new placesLib.PlacesService(dummyDiv);
		} catch (error) {
			console.error("Error initializing Google Places services:", error);
		}
	}, [placesLib, disabled]);

	// Handle input change and fetch predictions
	const handleInputChange = useCallback(
		(_: React.SyntheticEvent, newInputValue: string) => {
			setInputValue(newInputValue);

			// Don't search if input is too short
			if (!newInputValue || newInputValue.length < minInputLength) {
				setOptions([]);
				return;
			}

			// Don't search if services are not ready
			if (!autocompleteService.current || !placesService.current) {
				return;
			}

			setIsLoading(true);

			// Get predictions from Google Places
			autocompleteService.current.getPlacePredictions(
				{
					input: newInputValue,
					types,
					componentRestrictions,
					language: i18n.language // Add current language from i18n
				},
				(predictions, status) => {
					setIsLoading(false);

					if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
						setOptions([]);
						return;
					}

					// Convert predictions to City objects
					const cityOptions: google.maps.places.PlaceResult[] = predictions.map(
						prediction =>
							({
								place_id: prediction.place_id,
								formatted_address: prediction.description
							} as google.maps.places.PlaceResult)
					);

					setOptions(cityOptions);
				}
			);
		},
		[types, componentRestrictions, i18n.language, minInputLength]
	);

	// Handle city selection
	const handleCitySelect = useCallback(
		(_: React.SyntheticEvent, newValue: google.maps.places.PlaceResult | null) => {
			if (!newValue || !placesService.current) {
				onChange(null);
				return;
			}

			// Get detailed place information
			placesService.current.getDetails(
				{
					placeId: newValue.place_id!,
					fields: memoizedFields,
					language: i18n.language
				},
				(place, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK && place) {
						onChange(place);
					} else {
						// Fallback to the basic city data we have
						onChange(null);
					}
				}
			);
		},
		[onChange, i18n.language, memoizedFields]
	);

	return (
		<Autocomplete<google.maps.places.PlaceResult, false, false, false>
			value={value}
			onChange={handleCitySelect}
			inputValue={inputValue}
			onInputChange={handleInputChange}
			options={options}
			getOptionLabel={option => option.formatted_address ?? ""}
			isOptionEqualToValue={(option, value) => option.place_id === value.place_id}
			loading={isLoading || loading}
			loadingText={t("placesAutocomplete.loadingPlaces")}
			noOptionsText={
				inputValue.length < minInputLength
					? t("placesAutocomplete.typeAtLeastChars", { count: minInputLength })
					: t("placesAutocomplete.noElementFound")
			}
			disabled={disabled || !placesLib}
			renderInput={params => (
				<TextField
					{...params}
					{...textFieldProps}
					placeholder={placeholder}
					slotProps={{
						input: {
							...params.InputProps,
							startAdornment: (
								<>
									{startIcon || (
										<LocationIcon sx={{ color: "action.active", mr: 1, my: 0.5 }} />
									)}
									{params.InputProps?.startAdornment}
								</>
							),
							endAdornment: (
								<>
									{isLoading || loading ? (
										<CircularProgress color="inherit" size={20} />
									) : null}
									{params.InputProps?.endAdornment}
								</>
							)
						}
					}}
				/>
			)}
			renderOption={(props, option) => {
				const { key, ...otherProps } = props;
				return (
					<Box component="li" key={key} {...otherProps}>
						<LocationIcon sx={{ color: "action.active", mr: 2 }} />
						<Box>
							<Typography variant="body1">{option.formatted_address}</Typography>
						</Box>
					</Box>
				);
			}}
		/>
	);
};

export default PlacesAutocomplete;
